import { computed, Directive, effect, ElementRef, inject, input, OnInit, signal } from '@angular/core';
import type { MibMenuTriggerHost } from './menu.models';
import type { MibMenuComponent } from './menu.component';

/** Elements that can hold focus on their own; anything else delegates to the control it wraps. */
const FOCUSABLE = 'button, a[href], [tabindex]:not([tabindex="-1"])';

/**
 * Opens a {@link MibMenuComponent} from any element — a `[mibButton]`, an avatar, a bare `button`.
 *
 * ```html
 * <button mibButton [mibMenuTrigger]="actions">
 *   <svg mib-icon name="ellipsis-vertical"></svg>
 * </button>
 * <mib-menu #actions [menuItems]="items" (itemSelected)="onSelect($event)" />
 * ```
 *
 * Declare the panel **after** the trigger, as above, so that it registers before the panel refreshes
 * its own view.
 *
 * The trigger owns the open state and the ARIA wiring. Both are put on the element that actually
 * takes focus — the host itself when it is focusable, which is the usual case, otherwise the control
 * it wraps, for a trigger that is a wrapper around a button.
 */
@Directive({
  selector: '[mibMenuTrigger]',
  exportAs: 'mibMenuTrigger',
  host: {
    '(click)': 'toggle()',
    '(keydown)': 'onKeydown($event)',
  },
})
export class MibMenuTriggerDirective implements MibMenuTriggerHost, OnInit {
  /** The panel this element opens. */
  readonly panel = input.required<MibMenuComponent>({ alias: 'mibMenuTrigger' });

  /** The element the panel is anchored to. */
  readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly opened = signal(false);
  /** Whether the panel is open. */
  readonly expanded = this.opened.asReadonly();

  /** The element carrying focus and the ARIA state. */
  private readonly control = computed(() => {
    const host = this.elementRef.nativeElement;
    return host.matches(FOCUSABLE) ? host : (host.querySelector<HTMLElement>(FOCUSABLE) ?? host);
  });

  constructor() {
    effect(() => {
      const control = this.control();
      control.setAttribute('aria-haspopup', 'menu');
      control.setAttribute('aria-expanded', String(this.opened()));
    });
  }

  /*
   Registered here rather than in an effect: `ngOnInit` runs while the host element is being set up,
   which is before the panel declared after it refreshes its own view — so the panel never renders
   the built-in button it is about to drop.
  */
  /** Registers this element as the trigger of its panel. */
  ngOnInit(): void {
    this.panel().attachTrigger(this);
  }

  /** Opens the panel when it is closed, closes it otherwise. */
  toggle(): void {
    this.opened.update((open) => !open);
  }

  /** Opens the panel. */
  open(): void {
    this.opened.set(true);
  }

  /** Closes the panel, leaving focus where it is. */
  close(): void {
    this.opened.set(false);
  }

  /** Puts focus back where the user left it, which is what closing a menu owes them. */
  focus(): void {
    this.control().focus();
  }

  protected onKeydown(event: KeyboardEvent): void {
    /*
     Enter and Space are left alone: on a real button the browser turns them into a click, which the
     host listener already handles, and intercepting them here would toggle the menu twice.
    */
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.open();
      return;
    }
    if (event.key === 'Escape' && this.opened()) {
      event.preventDefault();
      this.close();
    }
  }
}
